import { Component, Input } from '@angular/core';

import { ReviewStats } from '../../interfaces/review-stats.interface';
import {
  CountVariation,
  countVariation,
  formatCount,
  formatDurationSeconds,
  formatSharePercent,
  formatDecimal,
  withUnit } from '../../utils/review-stats-format.util';

/**
 * Cartões de resumo do dashboard de reviews: volume, achados por review e, na visão de
 * serviço, tempo até o resultado e falso positivo. A incidência de ação ALTA mora no card
 * de Risco (`ReviewStatsSeverityComponent`), com a variação contra o período anterior.
 * Espelha `CostsKpisComponent` no formato (widgets com valor em destaque + `po-tag` de
 * variação + `po-info` de apoio + ícone de ajuda), mas sem dinheiro — contagens
 * inteiras, sem a cautela de string que custo exige.
 *
 * Cada card responde a UMA pergunta: quanto entrou, quanto foi achado, quanto demorou. Um número preso perto do teto não tem mudança interpretável —
 * foi o que tirou do topo a taxa de conclusão e a de "reviews com achados" (92,7% em
 * produção). A falha operacional fica no bloco de saúde, com a população certa.
 */
@Component({
  selector: 'app-review-stats-kpis',
  templateUrl: './review-stats-kpis.component.html',
  styleUrls: ['./review-stats-kpis.component.css']
})
export class ReviewStatsKpisComponent {
  @Input() stats: ReviewStats | null = null;
  @Input() serviceView = false;
  /**
   * Falso positivo do time, na visão de serviço — dos votos do Painel de Curadoria. É o
   * número que diz se a ferramenta é útil ou ruído; nulo sem votos no período.
   */
  @Input() falsePositive: { rate: string; votes: string } | null = null;

  /**
   * A mesma resposta para a janela imediatamente anterior, de igual duração — a base
   * das variações. Chega `null` sem período fechado ("Todo o período") ou quando a
   * segunda chamada falhou: a comparação é acessório e não pode esconder os números do
   * período atual, então cada `*Change` some sozinho. Mesmo contrato do card de custo.
   */
  @Input() previous: ReviewStats | null = null;

  /**
   * Textos de ajuda (ⓘ) dos cards — mesmo padrão de `CostsKpisComponent.cardHelp`:
   * uma frase de abertura, depois uma entrada por campo exibido abaixo do valor.
   */
  readonly cardHelp = {
    totals:
      'Quantidade de vezes que um review foi solicitado no período. Se o mesmo changeset, Pull ' +
      'Request, issue, arquivo ou outro tipo de review for enviado de novo, conta como uma ' +
      'nova solicitação.\n\n' +
      '• Recursos distintos: changesets, Pull Requests, issues, arquivos e outros ' +
      'recursos diferentes enviados. Reenviar o mesmo recurso de novo não é contabilizado aqui — ' +
      'só na solicitação.\n' +
      '• Solicitações concluídas: solicitações que concluíram o processamento do código e a análise ' +
      'do assistente. Itens na fila, em processamento ou com falha não entram neste total.\n' +
      '• vs. período anterior: comparação com a janela imediatamente anterior, de mesma duração.',
    findings:
      'Quantos achados um review típico teve no período.\n\n' +
      '• Mediana: ordena todos os reviews do menor pro maior achado e pega o valor bem no meio ' +
      'da lista.\n' +
      '• P90: 9 de cada 10 reviews tiveram até este número — só 1 em cada 10 teve mais.\n' +
      '• Média: soma de tudo dividida pelos reviews.\n' +
      '• Dado incompleto: review sem contagem de achados ainda — não conta como poucos achados, ' +
      'fica de fora da conta.\n' +
      '• vs. período anterior: quanto a mediana mudou desde a janela anterior, de mesma duração.',
    falsePositive:
      'Votos "não procede" sobre o total de votos em achados, nos filtros escolhidos. É o que diz se ' +
      'o assistente está ajudando ou gerando ruído.\n\n' +
      '• Mesmo número do card Calibragem, abaixo.\n' +
      '• Sem votos no período, fica "—".',
    duration:
      'Tempo entre o pedido e o resultado, com a fila incluída: quanto quem pediu esperou.\n\n' +
      '• Medido pelo registro de execuções do assistente, que nunca é reescrito.\n' +
      '• Pedido que reaproveitou um resultado anterior não entra.\n' +
      '• P90: 9 de cada 10 reviews terminaram até este tempo — só 1 em cada 10 demorou mais.\n' +
      '• Mediana: valor típico, exibido como contexto.'
  };

  /**
   * A largura de cada card, para a linha fechar sem buraco. O dev tem dois — volume e
   * achados; a incidência de ação ALTA é o card de Risco logo abaixo, e repeti-la aqui
   * mostrava o mesmo número duas vezes na tela.
   */
  get cardClass(): string {
    if (!this.serviceView) { return 'po-md-6'; }
    // Serviço: volume, achados, tempo e falso positivo — quatro na linha.
    return this.hasDurationBlock ? 'po-md-3' : 'po-md-4';
  }

  /** Só há o que mostrar depois de uma resposta — evita a linha "— de —" no vazio. */
  get hasTotals(): boolean {
    return !!this.stats?.totals;
  }

  // ─── Volume ──────────────────────────────────────────────────────────────

  get totalLabel(): string {
    return formatCount(this.stats?.totals?.total);
  }

  get totalChange(): CountVariation | null {
    return countVariation(this.stats?.totals?.total, this.previous?.totals?.total);
  }

  get previousTotalLabel(): string {
    return formatCount(this.previous?.totals?.total);
  }

  get uniqueResourcesLabel(): string {
    return formatCount(this.stats?.totals?.uniqueResources);
  }

  get doneLabel(): string {
    const totals = this.stats?.totals;
    if (!totals) { return '—'; }
    return `${formatCount(totals.done)} de ${formatCount(totals.total)}`;
  }

  // ─── Achados ─────────────────────────────────────────────────────────────

  get findingsTotalLabel(): string {
    return formatCount(this.stats?.findings?.total);
  }

  /**
   * Variação da MEDIANA, não do total de achados — o card destaca a mediana, e o total
   * já tem a variação dele no card "Solicitações de review" (mais reviews, mais achados,
   * quase sempre, sem dizer nada sobre a qualidade do código).
   */
  get findingsChange(): CountVariation | null {
    return countVariation(
      Number(this.stats?.findings?.perReviewMedian), Number(this.previous?.findings?.perReviewMedian)
    );
  }

  get findingsMedianLabel(): string {
    return formatDecimal(this.stats?.findings?.perReviewMedian);
  }

  get findingsP90Label(): string {
    return withUnit(formatDecimal(this.stats?.findings?.perReviewP90), 'achados');
  }

  get findingsPerReviewLabel(): string {
    return withUnit(formatDecimal(this.stats?.findings?.perReview), 'achados');
  }

  get findingsBaseLabel(): string {
    const findings = this.stats?.findings;
    if (!findings) { return '—'; }
    const reviews = findings.withFindings + findings.withoutFindings;
    const findingWord = findings.total === 1 ? 'achado' : 'achados';
    const reviewWord = reviews === 1 ? 'review' : 'reviews';
    return `${formatCount(findings.total)} ${findingWord} em ${formatCount(reviews)} ${reviewWord}`;
  }

  get hasFindingsSplit(): boolean {
    const findings = this.stats?.findings;
    return !!findings && findings.withFindings + findings.withoutFindings > 0;
  }

  get hasUnknownFindings(): boolean {
    return (this.stats?.findings?.unknownArtifacts ?? 0) > 0;
  }

  get unknownFindingsLabel(): string {
    const findings = this.stats?.findings;
    if (!findings) { return '—'; }
    const total = findings.knownArtifacts + findings.unknownArtifacts;
    return `${formatCount(findings.unknownArtifacts)} (${formatSharePercent(findings.unknownArtifacts, total)})`;
  }

  // ─── Duração ─────────────────────────────────────────────────────────────

  /**
   * Decide se o WIDGET inteiro aparece — não só o valor. `duration` só chega na
   * resposta para quem tem a capability `admin`/`superuser` (mesma regra de `health`);
   * a ausência do campo é a autoridade, e não há o que mostrar (nem "—") para quem
   * não a tem.
   */
  get hasDurationBlock(): boolean {
    return !!this.stats?.duration;
  }

  get hasDuration(): boolean {
    return this.stats?.duration?.medianSeconds != null;
  }

  get medianDurationLabel(): string {
    return formatDurationSeconds(this.stats?.duration?.medianSeconds);
  }

  get p90DurationLabel(): string {
    return formatDurationSeconds(this.stats?.duration?.p90Seconds);
  }

  get durationChange(): CountVariation | null {
    return countVariation(this.stats?.duration?.p90Seconds, this.previous?.duration?.p90Seconds);
  }
}
